import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { reachGoal } from "@/lib/metrika";

/**
 * Глобальный трекер вовлечённости — навешивается один раз внутри Router.
 * - На каждой смене пути сбрасывает таймеры и счётчики
 * - Шлёт Я.Метрике цели engagement_15s/30s/60s + scroll_25/50/75/100
 * - Уведомляет Метрику о SPA-навигации (`hit`) — это растит «глубину просмотра»
 */
export function EngagementTracker() {
  const location = useLocation();
  const firstHit = useRef(true);
  const notBounceFired = useRef(false);

  useEffect(() => {
    if (notBounceFired.current) return;
    const t = window.setTimeout(() => {
      notBounceFired.current = true;
      if (typeof window !== "undefined" && window.ym) {
        (window.ym as any)(106989563, "notBounce");
      }
    }, 10_000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // SPA hit (после первой загрузки)
    if (!firstHit.current && typeof window !== "undefined" && window.ym) {
      window.ym(106989563, "hit", window.location.href, {
        title: document.title,
        referer: document.referrer,
      });
    }
    firstHit.current = false;

    const fired = new Set<string>();
    const fire = (goal: string) => {
      if (fired.has(goal)) return;
      fired.add(goal);
      reachGoal(goal, { path: location.pathname });
    };

    const timers = [
      window.setTimeout(() => fire("engagement_15s"), 15_000),
      window.setTimeout(() => fire("engagement_30s"), 30_000),
      window.setTimeout(() => fire("engagement_60s"), 60_000),
    ];

    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const doc = document.documentElement;
        const max = Math.max(1, doc.scrollHeight - window.innerHeight);
        const pct = Math.round((window.scrollY / max) * 100);
        if (pct >= 25) fire("scroll_25");
        if (pct >= 50) fire("scroll_50");
        if (pct >= 75) fire("scroll_75");
        if (pct >= 95) fire("scroll_100");
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      timers.forEach(clearTimeout);
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [location.pathname]);

  return null;
}
